import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Palmtree, Plane, CheckSquare, Package, Calendar, MessageSquare } from 'lucide-react';

const API_URL = window.location.origin;

const RetreatHubPage = () => {
  const [hubData, setHubData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHubData();
  }, []);

  const fetchHubData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/retreat/hub`);
      setHubData(response.data);
    } catch (error) {
      console.error('Failed to fetch retreat hub:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="retreat-hub-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl bg-terracotta flex items-center justify-center">
              <Palmtree className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-forest">Just Be Costa Rica</h1>
              <Badge className="bg-terracotta/10 text-terracotta">
                Room: {hubData?.room_type || 'To be assigned'}
              </Badge>
            </div>
          </div>
          <p className="text-gray-600 mt-2">Your retreat hub with everything you need for your transformational journey.</p>
        </div>

        <Tabs defaultValue="itinerary" className="space-y-6">
          <TabsList className="bg-sand/50">
            <TabsTrigger value="itinerary">Itinerary</TabsTrigger>
            <TabsTrigger value="travel">Travel Checklist</TabsTrigger>
            <TabsTrigger value="packing">Packing List</TabsTrigger>
            <TabsTrigger value="integration">Integration</TabsTrigger>
          </TabsList>

          <TabsContent value="itinerary">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-terracotta" />
                  Retreat Itinerary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {hubData?.itinerary?.map((day, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-forest text-white flex flex-col items-center justify-center">
                        <span className="text-xs uppercase">Day</span>
                        <span className="text-2xl font-bold">{day.day}</span>
                      </div>
                      <div className="flex-1 p-4 bg-sand/30 rounded-xl">
                        <h3 className="font-heading font-semibold text-forest">{day.title}</h3>
                        <p className="text-gray-600 mt-1">{day.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="travel">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <Plane className="h-5 w-5 text-terracotta" />
                  Travel Checklist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {hubData?.travel_checklist?.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-sand/30 rounded-lg">
                      <CheckSquare className="h-5 w-5 text-forest" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="packing">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <Package className="h-5 w-5 text-terracotta" />
                  Packing List
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {hubData?.packing_list?.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-sand/30 rounded-lg">
                      <CheckSquare className="h-5 w-5 text-forest" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integration">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">
                  30-Day Integration Track
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-forest text-white rounded-xl p-6 mb-6">
                  <p className="text-white/80 mb-4">
                    Your transformation doesn't end when you leave Costa Rica. 
                    This 30-day integration track helps you carry the magic home.
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold">{hubData?.integration_track?.current_day || 0}</p>
                      <p className="text-xs text-white/60">Current Day</p>
                    </div>
                    <div className="flex-1 h-2 bg-white/20 rounded-full">
                      <div 
                        className="h-2 bg-terracotta rounded-full"
                        style={{ width: `${((hubData?.integration_track?.current_day || 0) / 30) * 100}%` }}
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold">30</p>
                      <p className="text-xs text-white/60">Total Days</p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-500 text-center">
                  Integration prompts and resources will appear here during your 30-day journey.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Retreat Community */}
        <Card className="card-base mt-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-terracotta" />
              Retreat Community
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Connect with your retreat sisters. Share excitement, coordinate travel, and build bonds before we meet.
            </p>
            <a href="/circles/vip-costa-rica">
              <button className="btn-primary px-6 py-2 rounded-lg">
                Join Retreat Chat
              </button>
            </a>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default RetreatHubPage;
